// A deliberately minimal test surface for the rebuilt redlining engine.
//
// WHAT THIS IS. The owner asked to test the new engine. It has a complete API
// (analyze -> review -> generate -> download) and no screen at all; the product's
// existing redlining UI drives the OLD engine through a different path. This page is the
// smallest honest way to put the new engine in front of him: pick a request, a document
// version and a playbook, run the review, read every proposed operation with its
// rationale, generate the tracked-changes document, download it, open it in Word.
//
// WHAT THIS IS NOT. Not the product UI, and it says so on its face. It is reachable
// only by the direct #redline-v2-test hash — no navigation entry — and the backend
// refuses everything unless REDLINE_V2_ENABLED is set, which is dev-only. Nothing here
// renders unless that flag answers.

import React, { useCallback, useEffect, useMemo, useState } from "react";

const token = () => localStorage.getItem("kontractai_token");
const headers = () => ({
  "Content-Type": "application/json",
  ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
});

async function api(path: string, init?: RequestInit) {
  const response = await fetch(path, { headers: headers(), ...init });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || body.error || `Request failed (${response.status})`);
  }
  return body;
}

function download(path: string, fallbackName: string) {
  return fetch(path, { headers: token() ? { Authorization: `Bearer ${token()}` } : {} })
    .then(async (response) => {
      if (!response.ok) throw new Error("The download could not be prepared.");
      const disposition = response.headers.get("Content-Disposition") || "";
      const filename = disposition.match(/filename="([^"]+)"/i)?.[1] || fallbackName;
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = filename.replace(/[<>:"/\\|?*\r\n]/g, "_");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
}

type Stage = "pick" | "analyzing" | "review" | "generating" | "done";

export function RedlineV2TestView() {
  const [requests, setRequests] = useState<any[]>([]);
  const [playbooks, setPlaybooks] = useState<any[]>([]);
  const [requestId, setRequestId] = useState("");
  const [versions, setVersions] = useState<any[]>([]);
  const [versionId, setVersionId] = useState("");
  const [playbookSlug, setPlaybookSlug] = useState("");
  const [partyRole, setPartyRole] = useState("");
  const [stage, setStage] = useState<Stage>("pick");
  const [review, setReview] = useState<any>(null);
  const [error, setError] = useState("");
  const [progressNote, setProgressNote] = useState("");

  useEffect(() => {
    api("/api/requests").then((body) => setRequests(body.requests || [])).catch((e) => setError(e.message));
    api("/api/playbooks").then((body) => setPlaybooks((body.playbooks || []).filter((p: any) => p.is_active !== false))).catch(() => setPlaybooks([]));
  }, []);

  useEffect(() => {
    if (!requestId) { setVersions([]); return; }
    api(`/api/requests/${requestId}`)
      .then((body) => {
        const docx = (body.versions || []).filter((v: any) =>
          String(v.file_type || "").toLowerCase() === "docx" || String(v.filename || "").toLowerCase().endsWith(".docx"));
        setVersions(docx);
        if (docx.length === 1) setVersionId(docx[0].id);
      })
      .catch((e) => setError(e.message));
  }, [requestId]);

  const canAnalyze = requestId && versionId && playbookSlug && partyRole.trim();

  const analyze = useCallback(async () => {
    setError("");
    setStage("analyzing");
    setProgressNote("Reading the playbook, checking it belongs to this client, and reviewing every paragraph. This takes a minute or two.");
    try {
      const created = await api("/api/redline-v2/analyze", {
        method: "POST",
        body: JSON.stringify({
          request_id: requestId,
          version_id: versionId,
          playbook_slug: playbookSlug,
          party_role: partyRole.trim(),
        }),
      });
      // The API nests the session under `review`: analyze returns { review: {...} }
      // and GET /:id returns { review, operations, artifacts }. Keep the id at the
      // top level of our state so generate/download can address the session.
      const reviewId = created?.review?.id || created?.id;
      if (!reviewId) throw new Error("The review was created but no review id came back.");
      const full = await api(`/api/redline-v2/${reviewId}`);
      if (full?.review?.status === "failed") {
        throw new Error(full?.review?.error_json?.message || "The analysis failed — see the server logs.");
      }
      setReview({ ...full, id: reviewId });
      setStage("review");
    } catch (e: any) {
      setError(e.message);
      setStage("pick");
    }
  }, [requestId, versionId, playbookSlug, partyRole]);

  const generate = useCallback(async () => {
    setError("");
    setStage("generating");
    setProgressNote("Writing the tracked changes into the document and running the release checks.");
    try {
      await api(`/api/redline-v2/${review.id}/generate`, {
        method: "POST",
        body: JSON.stringify({ mode: "tracked_internal" }),
      });
      const full = await api(`/api/redline-v2/${review.id}`);
      setReview({ ...full, id: review.id });
      setStage("done");
    } catch (e: any) {
      setError(e.message);
      setStage("review");
    }
  }, [review]);

  const operations = useMemo(() => review?.operations || [], [review]);
  const artifacts = useMemo(() => review?.artifacts || [], [review]);
  const pendingCount = useMemo(
    () => operations.filter((op: any) => (op.decision || "pending") === "pending").length,
    [operations],
  );

  // Generation refuses while any operation is undecided (409 pending_decisions), so
  // the page must let the reviewer decide each one. Accept/reject only here — the
  // full product screen will carry modification; this is a test surface.
  const decide = useCallback(async (operationId: string, decision: "accepted" | "rejected") => {
    setError("");
    try {
      const body = await api(`/api/redline-v2/${review.id}/operations/${operationId}`, {
        method: "PATCH",
        body: JSON.stringify({ decision }),
      });
      setReview((prev: any) => ({
        ...prev,
        operations: (prev.operations || []).map((op: any) => (op.id === operationId ? { ...op, ...body.operation } : op)),
      }));
    } catch (e: any) {
      setError(e.message);
    }
  }, [review]);

  const decideAll = useCallback(async (decision: "accepted" | "rejected") => {
    for (const op of operations) {
      if ((op.decision || "pending") === "pending") {
        // Sequential on purpose: each PATCH is cheap and this keeps failures attributable.
        // eslint-disable-next-line no-await-in-loop
        await decide(op.id, decision);
      }
    }
  }, [operations, decide]);

  return (
    <div style={{ maxWidth: 900, margin: "24px auto", padding: 16, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: "#FFF7E0", border: "1px solid #E5C558", borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <strong>Test surface — new redlining engine.</strong> This page exists so the rebuilt engine
        can be exercised before it has a product screen. It is reachable only by direct link and only
        where the engine is switched on. Nothing here affects the product's existing redlining.
      </div>

      {error && (
        <div style={{ background: "#FDECEA", border: "1px solid #E57373", borderRadius: 8, padding: 12, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {stage === "pick" && (
        <div style={{ display: "grid", gap: 12 }}>
          <label>
            <div>Contract request</div>
            <select value={requestId} onChange={(e) => { setRequestId(e.target.value); setVersionId(""); }} style={{ width: "100%", padding: 8 }}>
              <option value="">Choose a request…</option>
              {requests.map((r) => (
                <option key={r.id} value={r.id}>{r.title || r.display_number || r.id}</option>
              ))}
            </select>
          </label>
          <label>
            <div>Document version (Word documents only)</div>
            <select value={versionId} onChange={(e) => setVersionId(e.target.value)} style={{ width: "100%", padding: 8 }} disabled={!versions.length}>
              <option value="">{requestId ? (versions.length ? "Choose a version…" : "No Word documents on this request") : "Pick a request first"}</option>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>v{v.version_number} — {v.filename}</option>
              ))}
            </select>
          </label>
          <label>
            <div>Playbook</div>
            <select value={playbookSlug} onChange={(e) => setPlaybookSlug(e.target.value)} style={{ width: "100%", padding: 8 }}>
              <option value="">Choose a playbook…</option>
              {playbooks.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </label>
          <label>
            <div>Represented party — the client this review protects. The engine refuses a playbook written for a different client.</div>
            <input value={partyRole} onChange={(e) => setPartyRole(e.target.value)} placeholder='e.g. "Certara USA, Inc."' style={{ width: "100%", padding: 8 }} />
          </label>
          <button onClick={analyze} disabled={!canAnalyze} style={{ padding: "10px 16px", fontSize: 16 }}>
            Run the review
          </button>
        </div>
      )}

      {(stage === "analyzing" || stage === "generating") && (
        <div style={{ padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 18, marginBottom: 8 }}>Working…</div>
          <div style={{ color: "#666" }}>{progressNote}</div>
        </div>
      )}

      {(stage === "review" || stage === "done") && review && (
        <div>
          <h3>Proposed changes ({operations.length})</h3>
          {stage === "review" && pendingCount > 0 && (
            <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
              <span>{pendingCount} still need a decision.</span>
              <button onClick={() => decideAll("accepted")} style={{ padding: "6px 10px" }}>Accept all remaining</button>
            </div>
          )}
          {operations.map((op: any) => (
            <div key={op.id} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: "#666" }}>
                {op.category || op.action} · risk: {op.risk_level} · decision: <strong>{op.decision || "pending"}</strong>
              </div>
              {op.expected_text ? (
                <div style={{ margin: "6px 0" }}>
                  <span style={{ background: "#FDECEA", textDecoration: "line-through" }}>{op.expected_text}</span>{" "}
                  <span style={{ background: "#E8F5E9" }}>{op.replacement_text}</span>
                </div>
              ) : (
                <div style={{ margin: "6px 0", background: "#E8F5E9" }}>{op.replacement_text}</div>
              )}
              {op.rationale && <div style={{ fontSize: 13 }}>{op.rationale}</div>}
              {stage === "review" && (op.decision || "pending") === "pending" && (
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <button onClick={() => decide(op.id, "accepted")} style={{ padding: "6px 10px" }}>Accept</button>
                  <button onClick={() => decide(op.id, "rejected")} style={{ padding: "6px 10px" }}>Reject</button>
                </div>
              )}
            </div>
          ))}
          {stage === "review" && (
            <button onClick={generate} disabled={pendingCount > 0} style={{ padding: "10px 16px", fontSize: 16 }}>
              {pendingCount > 0 ? `Decide the remaining ${pendingCount} first` : "Generate the tracked-changes document"}
            </button>
          )}
          {stage === "done" && (
            <div>
              <h3>Documents</h3>
              {artifacts.map((a: any) => (
                <div key={a.id} style={{ marginBottom: 8 }}>
                  <button onClick={() => download(`/api/redline-v2/${review.id}/artifacts/${a.id}/download`, a.filename || "redline.docx")} style={{ padding: "8px 12px" }}>
                    Download {a.filename || a.output_mode || "redline"}
                  </button>
                </div>
              ))}
              {!artifacts.length && <div>No document was produced — see any error above.</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
